/**
 * SEO Configuration
 * 
 * Centralized SEO metadata for all pages.
 * Used by PageMeta component for consistent meta tag management.
 */

export interface PageMetaConfig {
  title: string;
  description: string;
  keywords?: string;
  canonicalPath?: string;
  ogImage?: string;
  ogType?: 'website' | 'article' | 'product';
  noindex?: boolean;
}

// Base site info
export const SITE_NAME = 'CastReady';
export const SITE_URL = 'https://hub.castready.org';
export const DEFAULT_OG_IMAGE = 'https://hub.castready.org/og-image.png';

// Main site URL for cross-domain references
export const MAIN_SITE_URL = 'https://castready.org';

/**
 * Wedge Messaging — single source of truth.
 *
 * WEDGE: Top-of-funnel hero, ads, investor copy. Names the primary audience
 *   and the work environments (film, TV, commercials, Broadway).
 * ECOSYSTEM_FULL: About, Press, Partnerships — full role list.
 * ECOSYSTEM_SHORT: Footer, OG defaults — under 200 chars.
 */
export const POSITIONING = 'The Actor Development Operating System.';
export const POSITIONING_SUBLINE =
  'Connecting actors, parents, coaches, studios, talent reps, and opportunities — on one platform.';
export const WEDGE =
  'CastReady is the Actor Development Operating System — built for actors, parents, coaches, studios, talent reps, and casting professionals working across film, television, commercials, and Broadway.';
export const ECOSYSTEM_FULL =
  'CastReady is the Actor Development Operating System — the connective tissue between training, readiness, and real industry work. Built for actors, parents, coaches, studios, talent reps, and casting professionals across film, television, commercials, and Broadway.';
export const ECOSYSTEM_SHORT =
  'The Actor Development Operating System — connecting actors, parents, coaches, studios, talent reps, and opportunities on one platform.';

/** Reusable keyword pack appended to wedge-relevant pages. */
export const WEDGE_KEYWORDS =
  'professional acting, film and TV auditions, Broadway auditions, commercial casting, talent representation, casting submissions, self-tape, actor career';

// Hreflang configuration - dormant-ready for i18n expansion
export const SUPPORTED_LOCALES = ['en'] as const;
export const DEFAULT_LOCALE = 'en';

// Generate hreflang entries for a page
export const getHreflangLinks = (canonicalUrl: string) => {
  return [
    { hreflang: 'en', href: canonicalUrl },
    { hreflang: 'x-default', href: canonicalUrl },
  ];
};

/**
 * Indexing allowlist — the only hub paths that should be indexable by search.
 *
 * Everything else on hub.castready.org returns `noindex, nofollow` via
 * `getPageMeta`. This keeps Google's crawl budget focused, prevents
 * thin/app pages from polluting SERPs, and stops hub from competing
 * with the marketing domain on shared queries.
 *
 * To add a new indexable page:
 * 1. Add the exact path (or `/prefix/*` pattern) here.
 * 2. Add a `PAGE_META` entry with a real title + description.
 * 3. Add a `<url>` entry to `public/sitemap.xml`.
 * 4. Make sure `domainConfig.ts` either has it canonical on `hub` or omits it.
 */
export const INDEXABLE_PATHS: ReadonlySet<string> = new Set([
  '/',
  '/pricing',
  '/training-kits',
  '/legal-center',
  '/trust-center',
  '/privacy-policy',
  '/terms-of-service',
  '/accessibility',
  '/support',
  '/status',
  '/security',
]);

/** Returns true if a path is in the explicit indexable allowlist. */
export const isIndexable = (pathname: string): boolean => {
  const clean = pathname.replace(/\/+$/, '') || '/';
  return INDEXABLE_PATHS.has(clean);
};

// Default meta config
export const DEFAULT_META: PageMetaConfig = {
  title: 'CastReady — The Actor Development Operating System',
  description: ECOSYSTEM_SHORT,
  keywords: `acting studio software, acting coach platform, student retention, casting platform, ${WEDGE_KEYWORDS}`,
  ogType: 'website',
};

// Page-specific meta configurations
export const PAGE_META: Record<string, PageMetaConfig> = {
  '/': {
    title: 'CastReady — The Actor Development Operating System',
    description: ECOSYSTEM_SHORT,
    keywords: `acting studio software, acting coach platform, student retention, youth actor coaching, COPPA compliant studio software, ${WEDGE_KEYWORDS}`,
  },
  '/pricing': {
    title: 'Pricing | Plans for Actors, Coaches, Casting & Reps',
    description: 'Plans built for actors, coaches, talent reps, and casting professionals working in film, television, commercials, and Broadway. Start free for 7 days.',
    keywords: `acting platform pricing, casting software pricing, talent agent software cost, ${WEDGE_KEYWORDS}`,
  },
  '/auth': {
    title: 'Sign In - CastReady',
    description: 'Sign in or create your CastReady account to start your acting journey.',
    noindex: true,
  },
  '/callback-zone': {
    title: 'Callback Zone Dashboard - CastReady',
    description: 'Your personal acting training dashboard. Track progress, complete lessons, and develop your craft.',
    noindex: true,
  },
  '/coach': {
    title: 'Coach Hub - CastReady',
    description: 'Manage your students, create lessons, and grow your coaching business with CastReady Coach Hub.',
    noindex: true,
  },
  '/pro': {
    title: 'CastReady Pro Dashboard',
    description: 'Professional casting and talent management tools for industry professionals.',
    noindex: true,
  },
  '/apply-qualification': {
    title: 'Apply for Pro Qualification - CastReady',
    description: 'Join CastReady Pro as a casting director, agent, manager, or producer. Verified professionals get access to premium casting tools.',
    keywords: 'casting director platform, talent agent tools, casting software, entertainment industry',
  },
  '/terms-of-service': {
    title: 'Terms of Service - CastReady',
    description: 'Read our terms of service governing your use of CastReady platforms.',
  },
  '/privacy-policy': {
    title: 'Privacy Policy - CastReady',
    description: 'Learn how CastReady protects your privacy and handles your personal information.',
  },
  '/contact': {
    title: 'Contact Us - CastReady',
    description: 'Get in touch with the CastReady team. We\'re here to help with questions about our acting training platforms.',
  },
  '/about': {
    title: 'About CastReady | The Actor Development Operating System',
    description: ECOSYSTEM_FULL,
    keywords: `about CastReady, performing arts platform, ${WEDGE_KEYWORDS}`,
  },
  '/faq': {
    title: 'FAQ - CastReady Help Center',
    description: 'Find answers to frequently asked questions about CastReady acting training, subscriptions, shipping, and youth safety.',
    keywords: 'CastReady FAQ, acting training help, subscription questions, COPPA compliant',
  },
  '/support': {
    title: 'Support Center - CastReady',
    description: 'Get help with your CastReady account, subscriptions, and acting training. Contact our support team.',
    keywords: 'CastReady support, customer service, help center, contact us',
  },
  '/legal-center': {
    title: 'Legal Center | Policies, Terms & Compliance | CastReady',
    description: 'CastReady\u2019s Legal Center: terms of service, privacy policy, COPPA/FERPA notices, DPA, and youth safety policies in one place.',
    keywords: 'CastReady legal, terms of service, privacy policy, COPPA, FERPA, DPA',
  },
  '/trust-center': {
    title: 'Trust Center | Privacy, Safety & Data Practices | CastReady',
    description: 'How CastReady protects actors, families, coaches, and partners \u2014 privacy controls, youth safety, vendor list, and subprocessor disclosures.',
    keywords: 'CastReady trust, data protection, youth safety, subprocessors, vendor disclosures',
  },
  '/status': {
    title: 'System Status | Platform Uptime & Incidents | CastReady',
    description: 'Live status, incident history, and scheduled maintenance for the CastReady platform \u2014 Callback Zone, Castings, Coach Hub, Pro, and more.',
    keywords: 'CastReady status, platform uptime, incidents, scheduled maintenance',
  },
  '/security': {
    title: 'Security at CastReady | Controls, MFA & Disclosure',
    description: 'CastReady\u2019s security program: encryption, MFA-protected admin, audit logging, kill switches, and responsible-disclosure contact.',
    keywords: 'CastReady security, MFA, encryption, vulnerability disclosure, SOC',
  },
  '/how-it-works': {
    title: 'How CastReady Works | From Practice to Professional Work',
    description: 'See how actors prepare, coaches train rosters, talent reps track clients, and casting teams fill roles in film, TV, commercials, and Broadway — all in one platform.',
    keywords: `how CastReady works, actor career path, ${WEDGE_KEYWORDS}`,
  },
  '/training-kits': {
    title: 'Acting Training Kits | Hands-On Boxes for Serious Actors',
    description: 'Hands-on training kits for actors building toward professional work in film, TV, commercials, and Broadway. Shipped with exclusive digital coaching content.',
    keywords: `acting training kit, actor training box, audition prep kit, ${WEDGE_KEYWORDS}`,
  },
  '/blog': {
    title: 'Blog & Resources - CastReady',
    description: 'Acting tips, audition guides, and resources for actors and families. Stay updated with CastReady insights.',
    keywords: 'acting blog, audition tips, actor resources, performing arts education',
  },
  '/blog/acting-education-is-broken': {
    title: 'Acting Education Is Broken for Too Many Kids | CastReady Blog',
    description: 'Exploring the systemic barriers in acting education and how we can build a more accessible, structured, and inclusive path for young performers.',
    keywords: 'acting education, child actors, accessible acting training, acting for kids, acting education reform',
    ogType: 'article',
  },
  '/blog/diversity-inclusion-in-acting': {
    title: 'The Role of Diversity and Inclusion in Acting Today | CastReady Blog',
    description: 'Diversity and inclusion in acting isn\'t a trend—it\'s a long-overdue shift toward stories that reflect the world we live in.',
    keywords: 'diversity in acting, inclusion in entertainment, representation in film, acting industry equity',
    ogType: 'article',
  },
  '/blog/recommended-books-videos': {
    title: 'Recommended Books & Videos for Actor Development | CastReady Blog',
    description: 'A curated list of books and video resources to help actors and families build strong fundamentals with less overwhelm.',
    keywords: 'acting books, actor resources, acting training videos, best books for actors, acting education',
    ogType: 'article',
  },
  '/our-technology': {
    title: 'Our Technology | AI That Assists Coaches, Not Replaces Them',
    description: 'Performance Insights help actors prepare and coaches teach faster. Human-first AI built for film, TV, commercials, and Broadway training.',
    keywords: 'AI acting coach, performance insights, human-first AI, acting technology',
  },
  '/ai-fairness': {
    title: 'AI Fairness & Cultural Inclusivity - CastReady',
    description: 'Our commitment to equitable, culturally-aware AI in acting education. Transparency about AI limitations and bias prevention.',
    keywords: 'AI fairness, cultural inclusivity, bias-free AI, diverse acting traditions',
  },
  '/partnerships': {
    title: 'Industry Partnerships | Coaches, Studios, Casting & Reps',
    description: ECOSYSTEM_FULL + ' Partner with us to reach actors and professionals across the performing arts.',
    keywords: `acting industry partnerships, studio partnerships, casting partnerships, ${WEDGE_KEYWORDS}`,
  },
  '/press': {
    title: 'Press & Media Kit | CastReady',
    description: ECOSYSTEM_FULL + ' Download brand assets, founder bio, and press materials.',
    keywords: 'CastReady press, media kit, press release, founder bio, performing arts platform',
  },
  '/accessibility': {
    title: 'Accessibility Statement - CastReady',
    description: 'CastReady\'s commitment to digital accessibility. Learn about our WCAG compliance and accessibility features.',
  },
  '/platforms/callback-zone': {
    title: 'Callback Zone | Daily Practice for Working Actors',
    description: 'Structured daily practice, audition prep, and a Casting Readiness score for actors pursuing film, TV, commercials, and Broadway work.',
    keywords: `Callback Zone, actor training platform, audition prep, ${WEDGE_KEYWORDS}`,
  },
  '/platforms/coach': {
    title: 'CastReady Coach | Run Your Studio in One Place',
    description: 'Roster, lessons, billing, and parent updates for coaches and studios training the next generation of film, TV, and Broadway performers.',
    keywords: 'acting coach platform, studio management, student roster, acting lesson planning',
  },
  '/platforms/castings': {
    title: 'CastReady Castings | Submit & Manage Auditions',
    description: 'Browse and submit to film, TV, commercial, and Broadway castings. Casting teams manage submissions, shortlists, and approvals at scale.',
    keywords: `casting calls, acting auditions, self-tape submission, ${WEDGE_KEYWORDS}`,
  },
  '/platforms/pro': {
    title: 'CastReady Pro | Invite-Only Industry Partner Portal',
    description: 'For verified casting directors, agents, managers, and industry partners. Manage talent, payouts, and partnerships across the CastReady ecosystem.',
    keywords: 'industry portal, casting directors, talent management, agent software, manager software',
  },
  // Persona Landing Pages
  '/for/parents': {
    title: 'Acting Training for Kids Pursuing Professional Work',
    description: 'Help your child train at home for real film, TV, commercial, and Broadway opportunities. Hands-on kits, Performance Insights, and a COPPA-safe community.',
    keywords: `acting classes for kids, child actor training, COPPA compliant, ${WEDGE_KEYWORDS}`,
  },
  '/for/adult-actors': {
    title: 'Acting Practice & Auditions for Working Actors',
    description: 'Daily practice, audition prep, and casting submissions for actors pursuing film, TV, commercials, and Broadway. Start your free trial.',
    keywords: `online acting practice, audition prep, ${WEDGE_KEYWORDS}`,
  },
  '/for/coaches': {
    title: 'Coach Software for Studios Training Working Actors',
    description: 'Roster, lessons, billing, and parent updates in one place — built for coaches developing actors for film, TV, commercials, and Broadway.',
    keywords: `acting coach software, studio management, ${WEDGE_KEYWORDS}`,
  },
  '/for/studios': {
    title: 'Studio Management Software for Performing Arts',
    description: 'Multi-instructor rosters, team analytics, and unified student communication for studios training the next wave of film, TV, and Broadway performers.',
    keywords: `acting studio software, multi-instructor management, performing arts school software, ${WEDGE_KEYWORDS}`,
  },
  '/for/talent-reps': {
    title: 'Talent Rep Software for Agents & Managers',
    description: 'Track client development, send prep assignments, and submit to film, TV, commercial, and Broadway castings — all in one rep workspace.',
    keywords: `talent agent software, manager software, actor roster management, ${WEDGE_KEYWORDS}`,
  },
  '/for/casting-pros': {
    title: 'Casting Software for Film, TV, Commercials & Broadway',
    description: 'Post breakdowns, manage submissions with AI-assisted screening, and fill roles faster across film, TV, commercials, and Broadway productions.',
    keywords: `casting software, casting director tools, submission management, ${WEDGE_KEYWORDS}`,
  },
  // HMU & Crew Platform Landing Pages
  '/platforms/hmu': {
    title: 'CastReady HMU - Hair, Makeup & Wardrobe for Performers',
    description: 'Book youth-safe HMU artists for self-tapes, headshots, and productions. Portfolio showcases, booking management, and ecosystem integration.',
    keywords: 'hair and makeup for actors, HMU artist booking, wardrobe styling, performer makeup, youth-safe HMU',
  },
  '/platforms/crew': {
    title: 'CastReady Crew - Production Crew Marketplace',
    description: 'Find verified, reviewed crew for your production. Reliability scores, youth safety certification, and seamless casting integration.',
    keywords: 'production crew marketplace, hire film crew, crew booking, production staffing, indie film crew',
  },
  // HMU & Crew Persona Landing Pages
  '/for/hmu-artists': {
    title: 'HMU Platform for Performing Arts Professionals',
    description: 'Grow your HMU career in the performing arts ecosystem. Build your portfolio, manage bookings, and connect with actors and productions.',
    keywords: 'HMU artist platform, makeup artist portfolio, hair stylist booking, wardrobe stylist, performing arts HMU',
  },
  '/for/producers': {
    title: 'Production Crew Staffing Software',
    description: 'Staff your production faster with verified, reviewed crew. Post projects, manage applications, and track reliability scores.',
    keywords: 'production staffing software, hire crew, indie producer tools, crew management, film production',
  },
  // HMU Authenticated Pages (noindex)
  '/hmu': {
    title: 'CastReady HMU Dashboard',
    description: 'Manage your HMU portfolio, bookings, and availability.',
    noindex: true,
  },
  // Crew Authenticated Pages (noindex)
  '/crew': {
    title: 'CastReady Crew Dashboard',
    description: 'Manage your crew profile, projects, and applications.',
    noindex: true,
  },
  // Draftline
  '/draftline': {
    title: 'CastReady Draftline Dashboard',
    description: 'Write, collaborate, and develop scripts with AI-powered tools.',
    noindex: true,
  },
  '/platforms/draftline': {
    title: 'CastReady Draftline - AI-Powered Screenwriting',
    description: 'Write better scripts faster with AI scene analysis, character development tools, and real-time collaboration. From first draft to final polish.',
    keywords: 'screenwriting software, AI script writing, screenplay tools, script collaboration, screenwriter platform',
  },
  // Demo
  '/demo-explore': {
    title: 'Explore CastReady Demo - Try All 8 Platforms',
    description: 'Experience the full CastReady ecosystem in demo mode. Try Callback Zone, Coach, Castings, Pro, Draftline, HMU, Capture, and Crew risk-free.',
    keywords: 'CastReady demo, try acting platform, free trial acting, explore CastReady',
  },
  // Persona Landing Pages - Screenwriters, Photographers, Crew Members
  '/for/screenwriters': {
    title: 'Screenwriting Software with AI | CastReady Draftline',
    description: 'Write better scripts faster with AI scene analysis, real-time collaboration, and character development tools. From first draft to final polish.',
    keywords: 'screenwriting software, AI script writing, screenplay collaboration, script development tools',
  },
  '/for/photographers': {
    title: 'Photography & Videography Platform for Talent',
    description: 'Grow your talent photography business. Build your portfolio, manage bookings, and connect with actors and productions across the CastReady ecosystem.',
    keywords: 'talent photographer platform, actor headshot photographer, portfolio photography booking',
  },
  '/for/crew-members': {
    title: 'Production Crew Jobs & Profile Platform',
    description: 'Get hired and build your reputation. Showcase reliability signals, earn youth-safe certification, and connect with productions across the CastReady ecosystem.',
    keywords: 'production crew jobs, film crew marketplace, crew member profile, crew booking platform',
  },
  '/platforms/capture': {
    title: 'CastReady Capture - Photography & Videography for Talent',
    description: 'Book photographers and videographers for headshots, demo reels, and portfolio shoots. Youth-safe, ecosystem-integrated.',
    keywords: 'talent photographer booking, actor headshots, demo reel videographer, performer photography',
  },
  // Comparison Pages
  '/compare/acting-apps': {
    title: 'Acting Practice Apps Comparison | CastReady',
    description: 'Compare CastReady to other acting training platforms. Find the right fit for structured, measurable acting practice at home.',
    keywords: 'acting apps comparison, StageMilk alternative, MasterClass alternative, Outschool alternative, acting training',
  },
  '/compare/castready-vs-stagemilk': {
    title: 'CastReady vs StageMilk | Acting Training Comparison',
    description: 'Compare CastReady and StageMilk for acting training. See how CastReady\'s hybrid approach with physical kits compares.',
    keywords: 'StageMilk alternative, CastReady vs StageMilk, acting training comparison',
  },
  '/compare/castready-vs-masterclass': {
    title: 'CastReady vs MasterClass | Acting Training Comparison',
    description: 'Compare CastReady and MasterClass for acting training. Interactive practice vs passive video learning.',
    keywords: 'MasterClass alternative, CastReady vs MasterClass, acting training comparison',
  },
  '/compare/castready-vs-outschool': {
    title: 'CastReady vs Outschool | Acting Training Comparison',
    description: 'Compare CastReady and Outschool for kids\' acting classes. Self-paced practice with kits vs live scheduled classes.',
    keywords: 'Outschool alternative, CastReady vs Outschool, acting classes for kids',
  },
  // Lead capture / high-intent pages
  '/book-demo': {
    title: 'Book a Demo | CastReady for Studios, Coaches & Casting',
    description: 'See CastReady live. 30-minute walkthrough tailored to studios, coaches, talent reps, and casting teams. Pick a time that works.',
    keywords: 'CastReady demo, book a demo, acting platform demo, studio software demo',
  },
  '/quiz/acting-readiness': {
    title: 'Acting Readiness Quiz | Is Your Child Ready to Audition?',
    description: 'Take the 2-minute readiness quiz. Get a personalized practice plan for film, TV, commercial, and Broadway auditions.',
    keywords: 'acting readiness quiz, is my child ready to audition, child actor assessment',
  },
  '/free/audition-checklist': {
    title: 'Free Audition Prep Checklist for Young Actors',
    description: 'Download the free audition prep checklist. Step-by-step from material selection to self-tape submission for film, TV, and commercials.',
    keywords: 'audition prep checklist, free actor checklist, self-tape checklist',
  },
};

/**
 * Get meta config for a given path
 */
export const getPageMeta = (path: string): PageMetaConfig => {
  const indexable = isIndexable(path);
  const defaults: PageMetaConfig = indexable
    ? DEFAULT_META
    : { ...DEFAULT_META, noindex: true };

  // Try exact match first
  if (PAGE_META[path]) {
    const merged = { ...defaults, ...PAGE_META[path] };
    // Indexable allowlist is authoritative — never accidentally index a non-allowlisted path
    if (!indexable) merged.noindex = true;
    return merged;
  }

  // Try to match parent path for nested routes
  const parentPath = '/' + path.split('/').slice(1, 2).join('/');
  if (PAGE_META[parentPath]) {
    const merged = { ...defaults, ...PAGE_META[parentPath] };
    if (!indexable) merged.noindex = true;
    return merged;
  }

  return defaults;
};

/**
 * Generate full page title with site name
 */
export const getFullTitle = (pageTitle: string): string => {
  if (pageTitle.includes(SITE_NAME)) {
    return pageTitle;
  }
  return `${pageTitle} | ${SITE_NAME}`;
};
